import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { InviteCodeStatus } from "../utils/constants"
import { useGraphQLClient } from "./useGraphQLClient"
import {
  GetInviteCodeByClientAndPractitionerDocument,
  GetInviteCodeByClientAndPractitionerQuery,
  GetInviteCodeByClientAndPractitionerQueryVariables,
} from "../generated/graphql"

export const useClientConnectionStatus = (
  clientId: string,
  practitionerId: string
) => {
  const graphqlClient = useGraphQLClient()

  // Query the invite_codes table directly
  const {
    data: inviteCodeData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["invite-code-status", clientId, practitionerId],
    queryFn: async () => {
      return await graphqlClient.request<
        GetInviteCodeByClientAndPractitionerQuery,
        GetInviteCodeByClientAndPractitionerQueryVariables
      >(GetInviteCodeByClientAndPractitionerDocument, {
        client_id: clientId,
        practitioner_id: practitionerId,
      })
    },
    enabled: !!clientId && !!practitionerId,
  })

  const connectionStatus = useMemo(() => {
    if (isLoading) {
      return {
        isConnected: false,
        canSchedule: false,
        status: null,
        isLoading: true,
        inviteCode: null,
        disconnectionDate: null,
      }
    }

    if (error || !inviteCodeData) {
      return {
        isConnected: false,
        canSchedule: false,
        status: null,
        isLoading: false,
        inviteCode: null,
        disconnectionDate: null,
        error: error || "No invite code found",
      }
    }
    if (inviteCodeData.invite_codes.length === 0) {
      // No invite codes found i.e. the supervisorId was used instead of practitionerId
      return {
        isConnected: true,
        canSchedule: true,
        status: null,
        isLoading: false,
        inviteCode: null,
        disconnectionDate: null,
        error: "No invite code found",
      }
    }

    const inviteCode = inviteCodeData.invite_codes[0]
    const status = inviteCode.status as InviteCodeStatus

    const isConnected = status === InviteCodeStatus.CONNECTED
    const isDisconnected =
      status === InviteCodeStatus.PRACTITIONER_DISCONNECTED ||
      status === InviteCodeStatus.CLIENT_DISCONNECTED

    // Check if there's an ended_at date indicating disconnection
    const hasEndedAt = !!inviteCode.ended_at
    const disconnectionDate = inviteCode.ended_at
      ? new Date(inviteCode.ended_at)
      : null

    return {
      isConnected,
      canSchedule: isConnected && !isDisconnected && !hasEndedAt,
      status,
      disconnectionReason: isDisconnected ? status : null,
      isLoading: false,
      inviteCode,
      disconnectionDate,
      hasEndedAt,
    }
  }, [inviteCodeData, isLoading, error])

  return connectionStatus
}

// Type definition for the return value
export interface ClientConnectionStatus {
  isConnected: boolean
  canSchedule: boolean
  status: InviteCodeStatus | null
  disconnectionReason?: string | null
  isLoading: boolean
  inviteCode?: any | null
  disconnectionDate?: Date | null
  hasEndedAt?: boolean
  error?: any
}
